import React from 'react';
import { Card, Progress, Tag } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { GroupedMaterialReservation } from '../types';
import { getDisplaySize } from '../../../../utils';
import {
	getDueDateColor,
	dueDateBorderColor,
} from '../../../../utils/dueDateColor';
import QueueClassTags from '../QueueClassTags';

const statusTagColor: Record<string, string> = {
	Cut: 'green',
	'In Progress': 'blue',
	'Not Cut': 'default',
};

/**
 * `group_status` is a rollup of the reservation cut statuses, not a statement
 * that anyone is working on this. A group holding one Cut and one Not Cut
 * reservation rolls up to "In Progress" with nothing in the saw, which is
 * exactly what Production called out: on a contradictory row that reads as
 * live work when the recorded status is itself part of the contradiction.
 *
 * So on a `needs_review` row the tag is neutral and says whose claim it is.
 * The class tag and the explanation beneath it carry what the operator should
 * actually do.
 */
const isDisputedStatus = (queueClass?: string) => queueClass === 'needs_review';

/**
 * A card under review recedes: the whole card greys out, not just its label.
 * These rows stay in the queue so a lead can find them, but an operator working
 * the saw should be able to scroll past one without reading it — and a card
 * that only differs by a tag colour does not let them.
 *
 * The due-date stripe greys out with the rest. A red stripe on a card nobody
 * should cut says the wrong thing twice: it draws the eye to the row, and it
 * implies the date is the thing to act on.
 */
const RECEDED_TEXT = '#9a9a9a';
const RECEDED_BACKGROUND = '#fcfcfc';
const RECEDED_BORDER = '#ececec';
const RECEDED_STRIPE = '#e0e0e0';

interface SawCutCardProps {
	record: GroupedMaterialReservation;
}

const SawCutCard: React.FC<SawCutCardProps> = ({ record }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const color = getDueDateColor(
		record.order_line_item_required_partner_ship_date
	);
	const cutPercent = record.total_material_cut_quantity
		? Math.round(
				(record.actual_cut_quantity / record.total_material_cut_quantity) * 100
		  )
		: 0;
	const progressStatus =
		cutPercent >= 100 ? 'success' : cutPercent > 0 ? 'active' : 'normal';
	// Pieces left is the number an operator picks work by: whether to clear a
	// quick job or load a bar and let the saw run. The server sends it, but the
	// large historical tabs skip classification, so fall back to the totals
	// rather than leaving the figure blank there.
	const remainingPieces =
		record.remaining_pieces ??
		Math.max(
			(record.total_material_cut_quantity ?? 0) -
				(record.actual_cut_quantity ?? 0),
			0
		);
	// A pending reservation whose recorded pieces already reached its planned
	// quantity leaves nothing to cut while still counting as work, so the row
	// stays actionable and leads with a large 0. That reads as "cut zero of
	// these" under a tag saying the job starts a machine, which is the
	// "looks already cut" confusion the queue exists to remove. QueueBarList
	// says it in words per bar; the headline says the same thing.
	const nothingLeftToCut = remainingPieces === 0;
	const receded = isDisputedStatus(record.queue_class);
	const labelColor = receded ? RECEDED_TEXT : '#888';
	const valueColor = receded ? RECEDED_TEXT : '#222';

	return (
		<Card
			data-testid="fastlane-saw-cut-card"
			data-cut-group-key={record.key}
			data-cut-card-receded={receded ? 'true' : undefined}
			style={{
				width: '100%',
				height: '100%',
				minWidth: 0,
				background: receded ? RECEDED_BACKGROUND : undefined,
				borderColor: receded ? RECEDED_BORDER : undefined,
				borderLeft: `4px solid ${
					receded ? RECEDED_STRIPE : dueDateBorderColor[color]
				}`,
				cursor: 'pointer',
			}}
			styles={{
				body: {
					padding: 16,
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
				},
			}}
			onClick={() =>
				navigate(`/tablet/operations/cutting/${record.key}`, {
					state: {
						record,
						returnTo: `/tablet/operations/cutting${location.search}`,
					},
				})
			}
		>
			<div
				className="flex flex-col gap-2"
				style={{
					flex: 1,
					minWidth: 0,
					minHeight: 0,
					overflow: 'hidden',
					display: 'flex',
					flexDirection: 'column',
					height: '100%',
				}}
			>
				<div
					className="flex items-center justify-between"
					style={{ marginBottom: 4 }}
				>
					<div style={{ fontSize: 16, color: labelColor }}>
						Due:{' '}
						<strong style={{ color: valueColor }}>
							{record.order_line_item_required_partner_ship_date
								? dayjs(
										record.order_line_item_required_partner_ship_date
								  ).format('MM/DD')
								: '—'}
						</strong>
					</div>
					<Tag
						data-testid="fastlane-cut-status"
						data-cut-group-key={record.key}
						data-cut-status={record.group_status}
						data-cut-status-disputed={receded ? 'true' : undefined}
						style={{ fontSize: 16, marginInlineEnd: 0 }}
						color={
							receded ? 'default' : statusTagColor[record.group_status] ?? 'default'
						}
					>
						{receded ? `Record: ${record.group_status}` : record.group_status}
					</Tag>
				</div>

				<QueueClassTags record={record} />

				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						marginBottom: 8,
						height: 220,
						border: '1px solid #f0f0f0',
						borderRadius: 8,
					}}
				>
					{record.thumbnail_file ? (
						<img
							src={record.thumbnail_file}
							alt="part thumbnail"
							style={{ width: 220, height: 220, objectFit: 'contain' }}
						/>
					) : (
						<span style={{ color: '#999', fontSize: 16 }}>No image</span>
					)}
				</div>

				<div
					style={{
						fontSize: 24,
						fontWeight: 700,
						lineHeight: 1.3,
						color: receded ? RECEDED_TEXT : undefined,
					}}
				>
					{record.order_name}
				</div>
				<div style={{ fontSize: 16, color: labelColor, minHeight: 30 }}>
					{record.order_line_item_name}
				</div>
				<div style={{ fontSize: 16, minHeight: 22 }}>
					<span style={{ color: labelColor }}>Reserving for:</span>{' '}
					<strong style={{ color: valueColor }}>
						{record.reserved_for_description?.trim() || '—'}
					</strong>
				</div>

				<div
					style={{
						fontSize: 16,
						paddingBottom: 4,
						borderBottom: '1px solid #f0f0f0',
						color: receded ? RECEDED_TEXT : undefined,
					}}
				>
					{record.material_type} · {record.material_shape} ·{' '}
					{getDisplaySize(record)}
				</div>

				<div
					style={{
						display: 'grid',
						gridTemplateColumns: '1fr 1fr',
						gap: 8,
						fontSize: 16,
						color: receded ? RECEDED_TEXT : undefined,
					}}
				>
					<div style={{ padding: '6px 0' }}>
						<span style={{ color: labelColor }}>Cut Len:</span>{' '}
						<strong>{record.material_cut_length}</strong>
					</div>
					<div style={{ padding: '6px 0' }}>
						<span style={{ color: labelColor }}>Qty:</span>{' '}
						<strong>{record.total_material_cut_quantity}</strong>
					</div>
					<div style={{ padding: '6px 0' }}>
						<span style={{ color: labelColor }}>Actual:</span>{' '}
						<strong>{record.actual_cut_quantity}</strong>
					</div>
					<div style={{ padding: '6px 0' }}>
						<span style={{ color: labelColor }}>Reserved:</span>{' '}
						<strong>{record.total_reserved_length}</strong>
					</div>
					<div style={{ padding: '6px 0', gridColumn: '1 / -1' }}>
						<span style={{ color: labelColor }}>Bars:</span>{' '}
						<strong>{record.reservations_count}</strong>
					</div>
				</div>

				<div
					style={{
						marginTop: 'auto',
						paddingTop: 8,
						width: '100%',
						flexShrink: 0,
					}}
				>
					<div
						className="flex items-baseline"
						style={{ gap: 8, flexWrap: 'wrap', marginBottom: 2 }}
					>
						{nothingLeftToCut ? (
							<span
								data-testid="fastlane-cut-remaining-pieces"
								style={{
									fontSize: 20,
									fontWeight: 600,
									lineHeight: 1.1,
									color: receded ? RECEDED_TEXT : labelColor,
								}}
							>
								Nothing left to cut
							</span>
						) : (
							<>
								<span
									data-testid="fastlane-cut-remaining-pieces"
									style={{
										fontSize: 34,
										fontWeight: 700,
										lineHeight: 1.1,
										color: receded ? RECEDED_TEXT : undefined,
									}}
								>
									{remainingPieces}
								</span>
								<span style={{ fontSize: 16, color: labelColor }}>
									{remainingPieces === 1 ? 'piece to cut' : 'pieces to cut'}
								</span>
							</>
						)}
						{record.long_runner && (
							<Tag
								data-testid="fastlane-cut-long-runner"
								color="geekblue"
								style={{ fontSize: 15, marginInlineEnd: 0 }}
							>
								Long runner
							</Tag>
						)}
					</div>
					<Progress
						percent={cutPercent}
						status={receded ? 'normal' : progressStatus}
						strokeColor={receded ? RECEDED_STRIPE : undefined}
						size="default"
						format={() => (
							<span style={{ color: receded ? RECEDED_TEXT : undefined }}>
								{record.actual_cut_quantity}/
								{record.total_material_cut_quantity}
							</span>
						)}
					/>
				</div>
			</div>
		</Card>
	);
};

export default SawCutCard;
